
import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import { Dashboard } from './components/Dashboard'; 
import KanzleiBotDemo from './components/KanzleiBotDemo';
import ROICalculator from './components/ROICalculator'; 
import WorkflowMonitor from './components/WorkflowMonitor'; 
import SalesPipeline from './components/SalesPipeline';
import LandingPage from './components/LandingPage';
import LandingPreviewIndex from './components/landing-templates/LandingPreviewIndex';
import LandingTemplatePreviewShell from './components/landing-templates/LandingTemplatePreviewShell';
import {
  getDefaultLandingTemplate,
  LANDING_PREVIEWS_ENABLED,
  LANDING_TEMPLATES,
} from './components/landing-templates/registry';
import { T05_PRODUCT_ROUTES } from './components/products/t05/routes.fragment';
import DeepAudit from './components/DeepAudit';
import AffiliatePortal from './components/AffiliatePortal'; 
import IntegrationsHub from './components/IntegrationsHub'; 
import SalesPlaybook from './components/SalesPlaybook';
import BrainUpload from './components/BrainUpload';
import CustomerLogin, { CustomerRecord } from './components/CustomerLogin';
import CustomerPortal from './components/CustomerPortal';
import { CEOGateLogin, hasValidCeoSession, clearCeoSession } from './components/CEOGateLogin';
import DonnaWorkspace from './components/donna-workspace/DonnaWorkspace';
import GraphifyDevMap from './components/graphify/GraphifyDevMap';
import { AppView, Lead } from './types';
import {
  INITIAL_LEADS,
  PORTAL_POST_CHECKOUT_HINT_KEY,
  ROUTE_PRODUCT_PROFESSIONAL,
  ROUTE_PRODUCT_PREMIUM,
  LEGACY_ROUTES_TO_PROFESSIONAL,
  LEGACY_ROUTES_TO_PREMIUM,
  clearPortalPbSession,
  getPortalPbCustomerJson,
  getPortalPbToken,
  isPortalHostname,
  isDevPortalLoginPreview,
  isPortalPbSessionConsistent,
  navigateToPortalLogin,
} from './constants';
import { sendAdminNotification } from './services/n8nService';
import {
  createLeadIdempotencyKey,
  getLeadSubmitGuard,
  markLeadSubmitted,
} from './hooks/useLeadSubmitGuard';
import type { LeadSubmitResult, PublicLeadFormData } from './services/leadTypes';
import { installPortalTelemetryFetchInterceptor } from './components/portal/portalTelemetry';
import { Menu, CheckCircle2, ArrowRight } from 'lucide-react';
import { ArkorbitGridBackground } from './components/arkorbit-ui/ArkorbitGridBackground';
import VipBoxLanding from './components/VipBoxLanding';

const T05_ROUTE_ORBIS = '/produkt/orbis';
const T05_ROUTE_LITIGATION = '/produkt/litigation-engine';

const IS_HEALTH_MAP_PATH =
  typeof window !== 'undefined' && /^\/health-map(\/|$)/.test(window.location.pathname);
const IS_PORTAL =
  typeof window !== 'undefined' &&
  !IS_HEALTH_MAP_PATH &&
  (isPortalHostname(window.location.hostname) || isDevPortalLoginPreview());

const App: React.FC = () => {
  useEffect(() => {
    console.log("ARKORBIT AI CORE V8.0");
  }, []);

  useEffect(() => {
    if (IS_PORTAL) installPortalTelemetryFetchInterceptor();
  }, []);

  /* ── KUNDEN-PORTAL MODE ── */
  const [portalCustomer, setPortalCustomer] = useState<CustomerRecord | null>(() => {
    if (!IS_PORTAL || typeof window === 'undefined') return null;

    const params = new URLSearchParams(window.location.search);
    const postCheckout =
      params.get('paid') === '1' ||
      params.get('welcome') === '1' ||
      params.get('checkout') === 'success';
    if (postCheckout) {
      sessionStorage.setItem(PORTAL_POST_CHECKOUT_HINT_KEY, '1');
    }

    // Link "Dashboard anmelden" mit ?forceLogin=1: immer Login-Maske zeigen (kein Sofort-Login)
    if (window.location.search.includes('forceLogin=1')) {
      clearPortalPbSession();
      try { window.history.replaceState({}, '', window.location.pathname); } catch {}
      return null;
    }
    if (postCheckout) {
      try { window.history.replaceState({}, '', window.location.pathname); } catch {}
    }

    if (!isPortalPbSessionConsistent()) {
      clearPortalPbSession();
      return null;
    }
    const stored = getPortalPbCustomerJson();
    const token = getPortalPbToken();
    if (stored && token) { try { return JSON.parse(stored); } catch { return null; } }
    return null;
  });

  // Stabil: Inline-Callbacks würden sonst bei jedem setPortalCustomer neu entstehen →
  // CustomerPortal handleLogout / auth-refresh-Effect → Endlos-Reload (Sitze-UI Zucken 07.08.2026).
  const handlePortalLogin = useCallback((_token: string, customer: CustomerRecord) => {
    setPortalCustomer(customer);
  }, []);
  const handlePortalLogout = useCallback(() => {
    clearPortalPbSession();
    setPortalCustomer(null);
  }, []);
  const handlePortalCustomerUpdate = useCallback((updated: CustomerRecord) => {
    setPortalCustomer(updated);
  }, []);

  if (IS_PORTAL) {
    if (!portalCustomer) {
      return <CustomerLogin onLogin={handlePortalLogin} />;
    }
    return (
      <CustomerPortal
        customer={portalCustomer}
        onLogout={handlePortalLogout}
        onCustomerUpdate={handlePortalCustomerUpdate}
      />
    );
  }
  /* ── END PORTAL MODE ── */

  const [isPublicMode, setIsPublicMode] = useState(true);
  const [hasCeoAuth, setHasCeoAuth] = useState(() => hasValidCeoSession());
  const [currentView, setCurrentView] = useState<AppView>(AppView.DASHBOARD);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);

  useEffect(() => {
      const params = new URLSearchParams(window.location.search);
      
      const refCode = params.get('ref');
      if (refCode) {
          const expirationDate = new Date();
          expirationDate.setDate(expirationDate.getDate() + 60); 

          const trackingData = {
              code: refCode,
              expiry: expirationDate.getTime()
          };
          
          localStorage.setItem('orbit_ref_data', JSON.stringify(trackingData));
          console.log(`[Orbit Tracking] Partner ${refCode} locked in for 60 days.`);
      }

      if (params.get('payment') === 'success') {
          setShowSuccessScreen(true);
          setIsPublicMode(false); 
          
          const newUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
          window.history.pushState({path:newUrl},'',newUrl);
          
          sendAdminNotification('ALERT', {
              message: "NEUER KUNDE! Zahlung via Stripe erfolgreich.",
              plan: params.get('plan_id') || 'Unknown'
          });
      }
  }, []);
  
  const [leads, setLeads] = useState<Lead[]>(() => {
    const savedLeads = localStorage.getItem('orbit_leads');
    if (savedLeads) {
      try {
        return JSON.parse(savedLeads);
      } catch (e) {
        return INITIAL_LEADS;
      }
    }
    return INITIAL_LEADS;
  });

  useEffect(() => {
    localStorage.setItem('orbit_leads', JSON.stringify(leads));
  }, [leads]);

  const handleUpdateLeads = (newLeads: Lead[]) => {
    setLeads(newLeads);
  };

  const handlePublicLeadSubmit = async (formData: PublicLeadFormData): Promise<LeadSubmitResult> => {
    if (formData.website?.trim()) {
      return {
        ok: false,
        blocked: true,
        message: 'Die Anfrage konnte nicht gesendet werden.',
      };
    }

    const email = formData.email.trim();
    if (!email) {
      return { ok: false, status: 'error', message: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.' };
    }

    const guard = getLeadSubmitGuard(email);
    if (!guard.canSubmit) {
      return { ok: false, blocked: true, message: guard.message };
    }

    let affiliateCode = 'ORGANIC';
    const storedTracking = localStorage.getItem('orbit_ref_data');
    
    if (storedTracking) {
        try {
            const parsed = JSON.parse(storedTracking);
            if (new Date().getTime() < parsed.expiry) {
                affiliateCode = parsed.code;
            } else {
                localStorage.removeItem('orbit_ref_data'); 
            }
        } catch (e) {}
    }

    const newLead: Lead = {
        id: Date.now().toString(),
        name: formData.name,
        company: formData.company,
        status: 'New',
        value: 0, // Value determined after audit
        notes: `Inbound Audit Request. \nEmail: ${formData.email}\nInterest: ${formData.interest}\nReferral: ${affiliateCode}`
    };
    
    setLeads(prev => [newLead, ...prev]);

    const result = await sendAdminNotification('LEAD', {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        interest: formData.interest,
        quelle: formData.quelle || 'website',
        affiliateCode: affiliateCode,
        message: 'Neuer Audit-Request über Landing Page!',
        idempotencyKey: formData.idempotencyKey || createLeadIdempotencyKey(formData.email),
    });

    if (result.ok && result.pbSaved && result.pbId) {
      markLeadSubmitted(email);
    }

    return result;
  };

  const renderInternalContent = () => {
    if (showSuccessScreen) {
        return (
            <div className="h-full flex flex-col items-center justify-center animate-fade-in text-center max-w-2xl mx-auto">
                <div className="w-24 h-24 bg-emerald-500 rounded-full flex items-center justify-center mb-8 shadow-[0_0_50px_rgba(16,185,129,0.5)] animate-bounce">
                    <CheckCircle2 className="w-12 h-12 text-white" />
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">Projekt bestätigt!</h1>
                <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                    Ihr dedizierter <strong>Orbit Server</strong> wird provisioniert. 
                    Wir starten nun die Einrichtung der Nextcloud-Tunnel.
                </p>
                
                <button 
                    onClick={() => setShowSuccessScreen(false)}
                    className="bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 rounded-xl font-bold flex items-center transition-all hover:scale-105"
                >
                    Zum Dashboard <ArrowRight className="ml-2 w-5 h-5" />
                </button>
            </div>
        );
    }

    switch (currentView) {
      case AppView.DASHBOARD: return <Dashboard leads={leads} />;
      case AppView.DONNA_WORKSPACE: return <DonnaWorkspace />;
      case AppView.GRAPHIFY_DEV_MAP: return <GraphifyDevMap />;
      case AppView.DEEP_AUDIT: return <DeepAudit />;
      case AppView.INTEGRATIONS: return <IntegrationsHub />;
      case AppView.WORKFLOW_MONITOR: return <WorkflowMonitor />;
      case AppView.SALES_PLAYBOOK: return <SalesPlaybook />; 
      case AppView.BOT_DEMO: return <KanzleiBotDemo />;
      case AppView.ROI_CALCULATOR: return <ROICalculator />;
      case AppView.PIPELINE: return <SalesPipeline leads={leads} onUpdateLeads={handleUpdateLeads} />;
      case AppView.AFFILIATE_PORTAL: return <AffiliatePortal />;
      case AppView.BRAIN_UPLOAD: return <BrainUpload mode="ceo" />;
      default: return <Dashboard leads={leads} />;
    }
  };

  const handleNavigate = (view: AppView) => {
      setCurrentView(view);
      setIsMobileMenuOpen(false);
      setShowSuccessScreen(false);
  };

  const handleLoginClick = () => {
    navigateToPortalLogin();
  };

  const handleCeoLogout = () => {
    clearCeoSession();
    setHasCeoAuth(false);
    setIsPublicMode(true);
  };

  if (isPublicMode) {
    const defaultLanding = getDefaultLandingTemplate();
    const DefaultLandingComponent = defaultLanding?.Component;

    return (
        <BrowserRouter>
          <Routes>
            <Route path="/vip" element={<VipBoxLanding />} />
            {T05_PRODUCT_ROUTES.map(({ path, element: Page }) => (
              <Route
                key={path}
                path={path}
                element={
                  <Page
                    onLoginClick={handleLoginClick}
                    onLeadSubmit={handlePublicLeadSubmit}
                  />
                }
              />
            ))}
            <Route path={ROUTE_PRODUCT_PROFESSIONAL} element={<Navigate to={T05_ROUTE_ORBIS} replace />} />
            <Route path={ROUTE_PRODUCT_PREMIUM} element={<Navigate to={T05_ROUTE_LITIGATION} replace />} />
            {LEGACY_ROUTES_TO_PROFESSIONAL.map((path) => (
              <Route key={path} path={path} element={<Navigate to={T05_ROUTE_ORBIS} replace />} />
            ))}
            {LEGACY_ROUTES_TO_PREMIUM.map((path) => (
              <Route key={path} path={path} element={<Navigate to={T05_ROUTE_LITIGATION} replace />} />
            ))}
            {LANDING_PREVIEWS_ENABLED && (
              <>
                <Route path="/preview/landing" element={<LandingPreviewIndex />} />
                {LANDING_TEMPLATES.map(({ id, path, label, Component }) => (
                  <Route
                    key={id}
                    path={path}
                    element={
                      defaultLanding?.id === id ? (
                        <Navigate to="/" replace />
                      ) : (
                        <LandingTemplatePreviewShell templateId={id} label={label}>
                          <Component
                            onLoginClick={handleLoginClick}
                            onLeadSubmit={handlePublicLeadSubmit}
                          />
                        </LandingTemplatePreviewShell>
                      )
                    }
                  />
                ))}
              </>
            )}
            {DefaultLandingComponent ? (
              <>
                <Route
                  path="/"
                  element={
                    <DefaultLandingComponent
                      onLoginClick={handleLoginClick}
                      onLeadSubmit={handlePublicLeadSubmit}
                    />
                  }
                />
                <Route
                  path="*"
                  element={
                    <DefaultLandingComponent
                      onLoginClick={handleLoginClick}
                      onLeadSubmit={handlePublicLeadSubmit}
                    />
                  }
                />
              </>
            ) : (
              <Route
                path="*"
                element={
                  <LandingPage
                    onLoginClick={handleLoginClick}
                    onLeadSubmit={handlePublicLeadSubmit}
                  />
                }
              />
            )}
          </Routes>
        </BrowserRouter>
    );
  }

  if (!hasCeoAuth) {
    return (
      <CEOGateLogin
        onSuccess={() => setHasCeoAuth(true)}
        onBack={() => setIsPublicMode(true)}
      />
    );
  }

  return (
    <div className="flex h-screen bg-slate-950 text-slate-200 overflow-hidden font-sans">
      {isMobileMenuOpen && (
          <div 
            className="fixed inset-0 bg-black/60 z-40 md:hidden backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>
      )}

      <div className={`fixed inset-y-0 left-0 z-50 transform ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} md:relative md:translate-x-0 transition-transform duration-300 ease-in-out`}>
          <Sidebar 
            currentView={currentView} 
            onNavigate={handleNavigate} 
            onTogglePublic={handleCeoLogout}
            onCloseMobile={() => setIsMobileMenuOpen(false)} 
            leads={leads}
          />
      </div>
      
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
        <div className="h-1 w-full bg-gradient-to-r from-blue-600 via-purple-600 to-emerald-600 shadow-[0_2px_20px_rgba(37,99,235,0.3)] relative z-20"></div>
        
        <div className="md:hidden flex items-center p-4 bg-slate-900 border-b border-slate-800">
            <button onClick={() => setIsMobileMenuOpen(true)} className="text-white p-2 hover:bg-slate-800 rounded-lg">
                <Menu className="w-6 h-6" />
            </button>
            <div className="w-10 h-10 rounded-xl overflow-hidden border border-slate-700 flex-shrink-0 relative ml-2 bg-slate-900/80">
                <img src="/arkorbit-wappen.png" alt="ARKORBIT" className="absolute inset-0 w-full h-full object-cover object-center" style={{ transform: 'scale(1.9) translateY(23%)' }} />
            </div>
            <span className="ml-3 font-bold text-white tracking-wider">ARKORBIT AI</span>
        </div>
        
        <div className={`flex-1 min-h-0 relative bg-slate-950 ${
          currentView === AppView.DONNA_WORKSPACE || currentView === AppView.GRAPHIFY_DEV_MAP
            ? 'overflow-hidden'
            : 'overflow-y-auto p-4 md:p-8 scroll-smooth'
        }`}>
           {currentView !== AppView.DONNA_WORKSPACE && currentView !== AppView.GRAPHIFY_DEV_MAP && (
             <ArkorbitGridBackground opacity={0.04} />
           )}
           <div className={`relative z-10 h-full ${
             currentView === AppView.DONNA_WORKSPACE
               ? ''
               : currentView === AppView.GRAPHIFY_DEV_MAP
                 ? 'p-4 md:p-6 max-w-[100rem] mx-auto pb-20 md:pb-4'
                 : 'max-w-7xl mx-auto pb-20 md:pb-0'
           }`}>
            {renderInternalContent()}
           </div>
        </div>
      </main>
    </div>
  );
};

export default App;
